// /proyectos/:id/avisos — avisos y guías de mantención del proyecto
// (ADR-0152, fase 2, #0748). Hermana de /proyectos/:id/consultas.
//
// Arriba, el formulario: tipo (Aviso / Guía de mantención), título, texto,
// un PDF opcional y el envío (ahora o programado). «Vista previa» muestra
// cómo lo verá el propietario y a cuántos les llegará; recién ahí se
// confirma. Un aviso publicado no se edita: se retira (queda visible para
// el propietario como «Retirado») y, para corregir, se publica otro.
//
// Abajo, la lista: estado (programado, enviando, enviado, retirado,
// cancelado), «N de M entregados» y las acciones «Retirar» y «Cancelar
// programación», cada una con su confirmación en la fila.
//
// Quién la ve lo decide el servidor (jefe en sus proyectos, gerente en
// toda la org; 403/404 para el resto). Lo visual sale del DS del dashboard
// (panel, field, input, textarea, select, dtable, st-pill), como la
// pantalla de documentos.

import { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ANNOUNCEMENT_ATTACHMENT_CONTENT_TYPE,
  ANNOUNCEMENT_BODY_MAX,
  ANNOUNCEMENT_CATEGORIES,
  ANNOUNCEMENT_CATEGORY_LABEL,
  ANNOUNCEMENT_TITLE_MAX,
  type Announcement,
  type AnnouncementAudience,
  type AnnouncementCategory,
  type OrgMember,
} from "@bitacorapro/api-types";
import { Icon } from "../components/icons";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { SkelTable } from "../components/skeleton/SkelTable";
import { ApiError } from "../lib/api/error";
import { announcementsApi } from "../lib/api/endpoints/announcements";
import { putToStorage } from "../lib/api/endpoints/documents";
import { useCarteraProyectoDetail, useMemberships, useRecipe } from "../lib/api/queries";
import { useQueryView } from "../lib/loading/useQueryView";
import {
  cancelacionDe,
  entregaDe,
  envioDe,
  ESTADO_TONE,
  estadoLabel,
  faltaDeAviso,
  fechaHora,
  santiagoInstant,
  textoAudiencia,
  ventanaProgramacion,
  type AvisoDraft,
} from "../lib/postventa/avisos";
import { resolveUserDisplayName } from "../lib/util";
import { fileProblem } from "./DocumentosProyecto";
import { RefreshNotices, SlowNotice } from "./queryViewKit";

const avisosKey = (groupId: string) => ["project-announcements", groupId] as const;

const MB = 1024 * 1024;

function formatSize(bytes: number): string {
  return bytes >= MB
    ? `${(bytes / MB).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError)
    return err.status === 0 || err.status >= 500 ? fallback : err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

const BORRADOR_VACIO: AvisoDraft = { title: "", body: "", date: "", time: "", programar: false };

export function AvisosDelProyectoRoute() {
  const { id } = useParams<{ id: string }>();
  const { data: recipe } = useRecipe();
  const detail = useCarteraProyectoDetail(id, { limit: 1 });
  const groupLabel = recipe?.labels.assetGroup ?? "Proyecto";
  const groupPlural = recipe?.labels.assetGroupPlural ?? "Proyectos";
  const name = detail.data?.group.name ?? null;

  if (detail.error instanceof ApiError && detail.error.status === 404) {
    return (
      <div className="page-pad">
        <h1 className="page-header__title">{groupLabel} no encontrado</h1>
        <p className="page-header__sub">
          El id <code>{id}</code> no corresponde a ningún {groupLabel.toLowerCase()} de tu
          organización.
        </p>
        <Link to="/proyectos" className="btn btn--secondary">
          <Icon.Arrow s={14} /> Volver a {groupPlural.toLowerCase()}
        </Link>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            <Link to="/proyectos">{groupPlural}</Link>
            {name ? ` / ${name}` : ""}
          </div>
          <h1 className="page-header__title">Avisos</h1>
          <p className="page-header__sub">
            Avisos y guías de mantención para todos los propietarios del {groupLabel.toLowerCase()}.
            Llegan a la app y por correo. Una vez publicados no se editan: se retiran.
          </p>
        </div>
      </div>
      {id && <AvisosDelProyecto key={id} groupId={id} />}
    </div>
  );
}

/** El formulario y la lista de un proyecto. */
export function AvisosDelProyecto({ groupId }: { groupId: string }) {
  const list = useQuery({
    queryKey: avisosKey(groupId),
    queryFn: () => announcementsApi.list(groupId),
  });

  // ADR-0166: la lista pasa por el contrato de carga. El formulario se
  // dibuja de inmediato; sólo la lista se skeletoniza.
  const view = useQueryView(list);
  const retry = () => void list.refetch();

  if (view.kind === "error") {
    const err = view.error;
    return (
      <div className="panel">
        {view.offline ? (
          <PageError
            title="Sin conexión"
            description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
            onRetry={view.retry}
          />
        ) : (
          <PageError
            title={
              err instanceof ApiError && err.status === 403
                ? "No tienes acceso a los avisos de este proyecto"
                : "No pudimos cargar los avisos"
            }
            description={
              err instanceof ApiError && err.status === 403
                ? err.message
                : "Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
            }
            onRetry={view.retry}
          />
        )}
      </div>
    );
  }

  const data = view.kind === "ready" ? view.data : undefined;
  return (
    <div className="stack-gap">
      {view.kind === "ready" && (
        <RefreshNotices
          refreshFailed={view.refreshFailed}
          revalidatingHint={view.revalidatingHint}
          onRetry={retry}
        />
      )}
      <NuevoAviso
        groupId={groupId}
        audience={data?.audience ?? null}
        schedulingAvailable={data?.schedulingAvailable ?? false}
      />
      <ListaAvisos
        groupId={groupId}
        avisos={data?.announcements ?? null}
        cargando={view.kind === "loading" ? view : null}
        onRetry={retry}
      />
    </div>
  );
}

function NuevoAviso({
  groupId,
  audience,
  schedulingAvailable,
}: {
  groupId: string;
  audience: AnnouncementAudience | null;
  /** Sin el job de avisos en el ambiente, sólo «Enviar ahora». */
  schedulingAvailable: boolean;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState<AnnouncementCategory>("aviso");
  const [draft, setDraft] = useState<AvisoDraft>(BORRADOR_VACIO);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const ventana = useMemo(() => ventanaProgramacion(), []);

  // El intento en curso: la Idempotency-Key de la publicación y, si ya
  // subió, el PDF. Un reintento con el MISMO contenido reusa ambos (si la
  // publicación anterior sí entró, el servidor devuelve ese mismo aviso);
  // cambiar algo es otro intento. Sólo un éxito lo libera.
  const attemptRef = useRef<{ firma: string; key: string; objectKey: string | null } | null>(null);

  const falta = faltaDeAviso(draft, { audience, schedulingAvailable });
  const scheduledFor =
    draft.programar && draft.date && draft.time ? santiagoInstant(draft.date, draft.time) : null;

  const publish = useMutation({
    mutationFn: async () => {
      const body = {
        category,
        title: draft.title.trim(),
        body: draft.body.trim(),
        scheduledFor: scheduledFor ? scheduledFor.toISOString() : null,
      };
      const firma = JSON.stringify([body, file?.name, file?.size, file?.lastModified]);
      const prev = attemptRef.current;
      const attempt =
        prev && prev.firma === firma ? prev : { firma, key: crypto.randomUUID(), objectKey: null };
      attemptRef.current = attempt;
      if (file && attempt.objectKey === null) {
        const presign = await announcementsApi.attachmentUploadUrl(groupId, {
          contentType: ANNOUNCEMENT_ATTACHMENT_CONTENT_TYPE,
          contentLength: file.size,
        });
        const blob =
          file.type === ANNOUNCEMENT_ATTACHMENT_CONTENT_TYPE
            ? file
            : new Blob([file], { type: ANNOUNCEMENT_ATTACHMENT_CONTENT_TYPE });
        await putToStorage(presign, blob);
        attempt.objectKey = presign.objectKey;
      }
      return announcementsApi.create(
        groupId,
        { ...body, attachment: attempt.objectKey ? { objectKey: attempt.objectKey } : null },
        attempt.key
      );
    },
    onSuccess: (a) => {
      attemptRef.current = null;
      setDone(
        a.status === "programado" && a.scheduledFor
          ? `«${a.title}» quedó programado para el ${fechaHora(a.scheduledFor)}.`
          : `«${a.title}» quedó publicado. Los correos salen en los próximos minutos.`
      );
      setDraft(BORRADOR_VACIO);
      setFile(null);
      setPreview(false);
      if (fileRef.current) fileRef.current.value = "";
      void qc.invalidateQueries({ queryKey: avisosKey(groupId) });
    },
  });

  const editable = !publish.isPending;
  const set = (patch: Partial<AvisoDraft>) => {
    setDone(null);
    publish.reset();
    setPreview(false);
    setDraft((d) => ({ ...d, ...patch }));
  };

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    setDone(null);
    publish.reset();
    setPreview(false);
    setFile(picked);
    setFileError(picked ? fileProblem(picked) : null);
  }

  const puedeVer = falta === null && fileError === null && audience !== null;
  const tipo = ANNOUNCEMENT_CATEGORY_LABEL[category].toLowerCase();

  return (
    <section className="panel" aria-labelledby="avisos-nuevo">
      <div className="panel__head">
        <h2 className="panel__title" id="avisos-nuevo">
          Nuevo aviso
        </h2>
        <p className="panel__sub">
          {audience ? textoAudiencia(audience) : "Calculando a quién le llegará…"}
        </p>
      </div>
      <form
        className="panel__body stack-gap"
        onSubmit={(e) => {
          e.preventDefault();
          if (puedeVer) setPreview(true);
        }}
      >
        <div className="documents__fields">
          <div className="field">
            <label className="field__label" htmlFor="avisos-titulo">
              Título
            </label>
            <input
              id="avisos-titulo"
              className="input"
              type="text"
              maxLength={ANNOUNCEMENT_TITLE_MAX}
              value={draft.title}
              disabled={!editable}
              placeholder="Ej.: Corte de agua programado"
              onChange={(e) => set({ title: e.target.value })}
              aria-describedby="avisos-titulo-hint"
            />
            <span className="field__hint" id="avisos-titulo-hint">
              {draft.title.trim().length} de {ANNOUNCEMENT_TITLE_MAX} caracteres
            </span>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="avisos-tipo">
              Tipo
            </label>
            <select
              id="avisos-tipo"
              className="select"
              value={category}
              disabled={!editable}
              onChange={(e) => {
                setPreview(false);
                setCategory(e.target.value as AnnouncementCategory);
              }}
            >
              {ANNOUNCEMENT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {ANNOUNCEMENT_CATEGORY_LABEL[c]}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="avisos-texto">
            Texto
          </label>
          <textarea
            id="avisos-texto"
            className="textarea"
            rows={5}
            maxLength={ANNOUNCEMENT_BODY_MAX}
            value={draft.body}
            disabled={!editable}
            placeholder="Ej.: El sábado 18-oct, de 9:00 a 14:00, la empresa sanitaria hará trabajos en la red del condominio."
            onChange={(e) => set({ body: e.target.value })}
            aria-describedby="avisos-texto-hint"
          />
          <span className="field__hint" id="avisos-texto-hint">
            {draft.body.trim().length} de {ANNOUNCEMENT_BODY_MAX} caracteres. Texto simple: los
            saltos de línea se respetan.
          </span>
        </div>
        <div className="documents__fields">
          <div className="field">
            <label className="field__label" htmlFor="avisos-pdf">
              PDF adjunto (opcional)
            </label>
            <input
              id="avisos-pdf"
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              disabled={!editable}
              onChange={onPick}
              aria-describedby={fileError ? "avisos-pdf-problem" : "avisos-pdf-hint"}
            />
            {fileError ? (
              <p className="auth-error" id="avisos-pdf-problem" role="alert">
                {fileError}
              </p>
            ) : (
              <span className="field__hint" id="avisos-pdf-hint">
                {file
                  ? `${file.name} · ${formatSize(file.size)}`
                  : "Solo PDF, hasta 10 MB. El propietario lo abre desde el aviso."}
              </span>
            )}
          </div>
          {/* Sin el job que publica los programados, «Programar» no se
              ofrece (el servidor lo rechazaría). */}
          {schedulingAvailable && (
            <div className="field">
              <label className="field__label" htmlFor="avisos-envio">
                Envío
              </label>
              <select
                id="avisos-envio"
                className="select"
                value={draft.programar ? "programar" : "ahora"}
                disabled={!editable}
                onChange={(e) => set({ programar: e.target.value === "programar" })}
              >
                <option value="ahora">Enviar ahora</option>
                <option value="programar">Programar</option>
              </select>
            </div>
          )}
        </div>
        {draft.programar && (
          <div className="documents__fields">
            <div className="field">
              <label className="field__label" htmlFor="avisos-dia">
                Día
              </label>
              <input
                id="avisos-dia"
                className="input"
                type="date"
                min={ventana.min}
                max={ventana.max}
                value={draft.date}
                disabled={!editable}
                onChange={(e) => set({ date: e.target.value })}
              />
            </div>
            <div className="field">
              <label className="field__label" htmlFor="avisos-hora">
                Hora (Santiago)
              </label>
              <input
                id="avisos-hora"
                className="input"
                type="time"
                step={900}
                value={draft.time}
                disabled={!editable}
                onChange={(e) => set({ time: e.target.value.slice(0, 5) })}
              />
            </div>
          </div>
        )}

        {!preview && (
          <div className="documents__actions">
            <button type="submit" className="btn btn--primary" disabled={!puedeVer}>
              Vista previa
            </button>
            {falta && (draft.title || draft.body || draft.programar) && (
              <span className="cell-meta">{falta}</span>
            )}
          </div>
        )}

        {preview && audience && (
          <div className="avisos__confirm stack-gap" role="group" aria-labelledby="avisos-preview">
            <h3 className="panel__title" id="avisos-preview">
              Así lo verá el propietario
            </h3>
            <div className="avisos__preview">
              <div className="cell-meta">{ANNOUNCEMENT_CATEGORY_LABEL[category]}</div>
              <div className="cell-strong">{draft.title.trim()}</div>
              <p className="avisos__body">{draft.body.trim()}</p>
              {file && <div className="cell-meta">PDF adjunto: {file.name}</div>}
            </div>
            <p className="cell-meta">
              {scheduledFor
                ? `Se enviará el ${fechaHora(scheduledFor.toISOString())} a los propietarios con cuenta en ese momento, en la app y por correo.`
                : textoAudiencia(audience)}{" "}
              Una vez publicado no se puede editar: solo retirarlo, y queda registrado.
            </p>
            <div className="documents__actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={publish.isPending}
                onClick={() => publish.mutate()}
              >
                {publish.isPending
                  ? "Publicando…"
                  : scheduledFor
                    ? `Confirmar y programar ${tipo}`
                    : `Confirmar y enviar ${tipo}`}
              </button>
              <button
                type="button"
                className="btn btn--ghost"
                disabled={publish.isPending}
                onClick={() => setPreview(false)}
              >
                Volver a editar
              </button>
            </div>
          </div>
        )}
        {publish.isError && (
          <p className="auth-error" role="alert">
            {errorMessage(publish.error, "No pudimos publicar el aviso. Intenta de nuevo.")}
          </p>
        )}
        {done && (
          <p className="notice-banner" role="status">
            {done}
          </p>
        )}
      </form>
    </section>
  );
}

function ListaAvisos({
  groupId,
  avisos,
  cargando,
  onRetry,
}: {
  groupId: string;
  avisos: Announcement[] | null;
  /** La lista en carga (fase del skeleton); null cuando ya resolvió. */
  cargando: { visible: boolean; slow: boolean } | null;
  onRetry: () => void;
}) {
  const qc = useQueryClient();
  const memberships = useMemberships();
  const roster = useMemo(
    () =>
      new Map<string, Pick<OrgMember, "displayName">>(
        (memberships.data?.members ?? []).map((m) => [m.userId, m])
      ),
    [memberships.data]
  );
  const [confirm, setConfirm] = useState<{ id: string; verb: "retract" | "cancel" } | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  const act = useMutation({
    mutationFn: (c: { id: string; verb: "retract" | "cancel" }) =>
      c.verb === "retract"
        ? announcementsApi.retract(groupId, c.id)
        : announcementsApi.cancel(groupId, c.id),
    onSuccess: () => {
      setConfirm(null);
      void qc.invalidateQueries({ queryKey: avisosKey(groupId) });
    },
    onError: (err) => {
      // Otro lo cambió antes (salió, se retiró, se canceló): la lista está
      // desactualizada.
      if (err instanceof ApiError && (err.status === 404 || err.status === 409)) {
        setConfirm(null);
        void qc.invalidateQueries({ queryKey: avisosKey(groupId) });
      }
    },
  });

  async function abrirPdf(a: Announcement) {
    if (opening) return;
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    setOpening(a.id);
    setOpenError(null);
    try {
      const { url } = await announcementsApi.attachmentUrl(groupId, a.id);
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch (err) {
      tab?.close();
      setOpenError(errorMessage(err, "No pudimos abrir el PDF."));
    } finally {
      setOpening(null);
    }
  }

  const autor = (userId: string) =>
    roster.has(userId) ? resolveUserDisplayName(userId, roster) : null;

  return (
    <section className="panel" aria-labelledby="avisos-lista">
      <div className="panel__head">
        <h2 className="panel__title" id="avisos-lista">
          Publicados y programados
        </h2>
        <p className="panel__sub">Lo que se envió a los propietarios de este proyecto.</p>
      </div>
      <div className="panel__body stack-gap">
        {cargando && (
          <>
            <PageSkeleton label="Cargando avisos…" visible={cargando.visible}>
              <SkelTable rows={3} cols={5} headers={["Aviso", "Estado", "Envío", "Entrega", ""]} />
            </PageSkeleton>
            {cargando.slow && <SlowNotice onRetry={onRetry} />}
          </>
        )}
        {avisos !== null && avisos.length === 0 && (
          <p className="cell-meta">Todavía no hay avisos en este proyecto.</p>
        )}
        {openError && (
          <p className="auth-error" role="alert">
            {openError}
          </p>
        )}
        {act.isError && (
          <p className="auth-error" role="alert">
            {act.error instanceof ApiError && (act.error.status === 404 || act.error.status === 409)
              ? "El aviso cambió mientras tanto. Actualizamos la lista."
              : errorMessage(act.error, "No pudimos completar la acción.")}
          </p>
        )}
        {avisos !== null && avisos.length > 0 && (
          <table className="dtable">
            <thead>
              <tr>
                <th>Aviso</th>
                <th>Estado</th>
                <th>Envío</th>
                <th>Entrega</th>
                <th aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {avisos.map((a) => {
                const entrega = entregaDe(a);
                const quien = autor(a.createdBy);
                const retiradoPor = a.retractedBy ? autor(a.retractedBy) : null;
                const canceladoPor = a.cancelledBy ? autor(a.cancelledBy) : null;
                const porSistema = cancelacionDe(a);
                return (
                  <tr key={a.id}>
                    <td>
                      <div className="cell-strong">{a.title}</div>
                      <div className="cell-meta">
                        {[ANNOUNCEMENT_CATEGORY_LABEL[a.category], quien ? `por ${quien}` : null]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                      <p className="avisos__body avisos__body--clamp">{a.body}</p>
                      {a.retractedAt && (
                        <div className="cell-meta">
                          Retirado el {fechaHora(a.retractedAt)}
                          {retiradoPor ? ` por ${retiradoPor}` : ""}
                        </div>
                      )}
                      {a.cancelledAt && (
                        <div className="cell-meta">
                          {porSistema ??
                            `Cancelado el ${fechaHora(a.cancelledAt)}${canceladoPor ? ` por ${canceladoPor}` : ""}`}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`st-pill ${ESTADO_TONE[a.status]}`}>
                        {estadoLabel(a.status)}
                      </span>
                    </td>
                    <td>{envioDe(a)}</td>
                    <td>
                      <div>{entrega.principal}</div>
                      {entrega.detalle && <div className="cell-meta">{entrega.detalle}</div>}
                    </td>
                    <td>
                      {confirm?.id === a.id ? (
                        <div
                          className="documents__actions"
                          role="group"
                          aria-label={
                            confirm.verb === "retract"
                              ? "Confirmar retiro"
                              : "Confirmar cancelación"
                          }
                        >
                          <span className="cell-meta">
                            {confirm.verb === "retract"
                              ? "¿Retirar? Los propietarios lo verán como «Retirado»."
                              : "¿Cancelar? No se enviará."}
                          </span>
                          <button
                            type="button"
                            className="btn btn--primary btn--sm"
                            disabled={act.isPending}
                            onClick={() => act.mutate(confirm)}
                          >
                            {act.isPending
                              ? "Guardando…"
                              : confirm.verb === "retract"
                                ? "Sí, retirar"
                                : "Sí, cancelar"}
                          </button>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            disabled={act.isPending}
                            onClick={() => setConfirm(null)}
                          >
                            Volver
                          </button>
                        </div>
                      ) : (
                        <div className="documents__actions">
                          {a.attachment && (
                            <button
                              type="button"
                              className="btn btn--secondary btn--sm"
                              disabled={opening !== null}
                              onClick={() => void abrirPdf(a)}
                            >
                              <Icon.External s={12} />
                              {opening === a.id ? "Abriendo…" : "Ver PDF"}
                            </button>
                          )}
                          {(a.status === "enviando" || a.status === "enviado") && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => {
                                act.reset();
                                setConfirm({ id: a.id, verb: "retract" });
                              }}
                            >
                              Retirar
                            </button>
                          )}
                          {a.status === "programado" && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => {
                                act.reset();
                                setConfirm({ id: a.id, verb: "cancel" });
                              }}
                            >
                              Cancelar programación
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
